import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useToast } from '@/hooks/use-sonner-toast';
import { Element as ElementType } from '@/types';
import { useAuth } from './AuthContext';
import { ReportElementModal } from '@/components/ReportElementModal';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface ReportingModeContextType {
  isReportingMode: boolean;
  setIsReportingMode: (mode: boolean) => void;
  reportElementHandler: (element: ElementType) => Promise<void>;
}

// Create a default implementation that does nothing
const defaultReportingMode: ReportingModeContextType = {
  isReportingMode: false,
  setIsReportingMode: () => {
    // Silent no-op for better compatibility
  },
  reportElementHandler: async () => {
    // Silent no-op for better compatibility
  },
};

const ReportingModeContext = createContext<ReportingModeContextType>(defaultReportingMode);

export const useReportingMode = () => useContext(ReportingModeContext);

export const ReportingModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  
  const [isReportingMode, setIsReportingMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const reportElementHandler = async (element: ElementType) => {
    if (!currentUser) {
      toast({
        title: 'Sign in required',
        description: 'You need to be signed in to report elements.',
        variant: 'destructive',
      });
      setIsReportingMode(false);
      return;
    }

    // Set the selected element and open the modal
    setSelectedElement(element);
    setIsModalOpen(true);

    // Exit reporting mode
    setIsReportingMode(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedElement(null);
  };

  return (
    <ReportingModeContext.Provider
      value={{
        isReportingMode,
        setIsReportingMode,
        reportElementHandler,
      }}
    >
      {children}
      {isReportingMode && (
        <>
          {/* Semi-transparent overlay for everything except Elements & Discoveries */}
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 pointer-events-none">
            {/* This div is transparent and covers the entire screen */}
          </div>

          {/* Instruction banner at the top of the screen */}
          <div className="fixed top-0 left-0 right-0 bg-primary text-primary-foreground py-3 px-4 z-50 flex items-center justify-between shadow-lg">
            <div className="flex items-center">
              <AlertCircle className="mr-2 h-5 w-5" />
              <span className="font-medium">
                Reporting Mode: Click on any element in the Elements & Discoveries panel to report
                it.
              </span>
            </div>
            <Button
              variant="secondary"
              onClick={() => setIsReportingMode(false)}
              className="ml-4 pointer-events-auto"
            >
              Cancel
            </Button>
          </div>
        </>
      )}

      {/* Report Element Modal */}
      <ReportElementModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        element={selectedElement}
      />
    </ReportingModeContext.Provider>
  );
};

